import React, { useCallback, useEffect, useState } from "react";

import { debounce, Skeleton, Stack } from "@mui/material";
import { AsyncContentMui, Search } from "components";
import { useActionHook, useQueryHook } from "hooks";
import { useUserManageActions } from "store";
import { IUsersManageQueries } from "store/types/user-management";

import { Filters } from "./Filters/Filters";
import { Table } from "./Table/Table";

// Shared card look — the filter bar and the table sit in two separate white blocks (mirrors Contacts).
const cardSx = {
  bgcolor: "white",
  borderRadius: "8px",
  boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
  p: "16px"
} as const;

export const Users: React.FC = () => {
  const { getUsersAction, getUserPermissionListAction } = useUserManageActions();
  const { data, loading, action } = useActionHook(getUsersAction);
  const { data: permList = [] } = useQueryHook(getUserPermissionListAction);

  const [queries, setQueries] = useState<IUsersManageQueries>({ page: 1, per_page: 50 });

  const updateQueries = useCallback((data: Partial<IUsersManageQueries>) => {
    setQueries({ ...queries, ...data, page: 1 });
  }, []);

  const onSearch = useCallback(
    (search: string) => {
      updateQueries({ ...queries, search, page: 1 });
    },
    [updateQueries, queries]
  );

  const onSearchDebounce = debounce(onSearch, 500);

  const handleChangePage = useCallback(
    (newPage: number) => {
      setQueries((prev) => ({ ...prev, page: newPage + 1 })); //TablePAgination start from 0
    },
    [action]
  );

  useEffect(() => {
    action(queries);
  }, [queries]);

  return (
    <Stack sx={{ gap: "15px", flexGrow: 1, overflow: "hidden", height: "100%", width: "100%" }}>
      <Stack
        direction="row"
        sx={{ ...cardSx, justifyContent: "space-between", alignItems: "center", gap: "10px" }}
      >
        <Filters updateQueries={(data) => updateQueries({ ...queries, ...data })} appliedRoleIds={queries.role_id} />
        <div>
          <Search
            defaultValue={queries.search || ""}
            onChange={(e) => onSearchDebounce(e.target.value)}
            placeholder="Search"
          />
        </div>
      </Stack>

      <Stack sx={{ ...cardSx, flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
        <AsyncContentMui
          spinnerProps={{ size: 150 }}
          isLoading={loading}
          boxProps={{
            sx: {
              overflow: "hidden",
              height: "100%",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center"
            }
          }}
        >
          {loading ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height="100%"
              sx={{ flexGrow: 1, maxHeight: 160, minHeight: 160 }}
            />
          ) : (
            data && (
              <Table
                refetchTable={() => action(queries)}
                data={data}
                handleChangePage={handleChangePage}
                rowsPerPage={queries.per_page!}
                page={queries.page!}
                permissionList={permList}
              />
            )
          )}
        </AsyncContentMui>
      </Stack>
    </Stack>
  );
};
